import {
  MonitoringPromptKpi,
  MonitoringPromptPurposeType,
  TaxonomyTermPriority,
  TaxonomyTermStatus,
} from '@edgebox/sync-core-data-definitions';
import { termStatusPresentation, TermStatusPresentation } from './termStatusPresentation';

/**
 * The prompt-purpose tabs on the AI-visibility page.
 *
 * Purposes are a small, ordered set the operator works through, so they read as tabs rather than as
 * a dropdown — the choice is always visible, and the most important purpose is the one the page
 * opens on.
 */

/** A purpose term as the cached facet fetch returns it. */
export interface PurposeTermLike {
  id?: unknown;
  key?: unknown;
  name?: unknown;
  status?: TaxonomyTermStatus;
  priority?: TaxonomyTermPriority;
  /** `null` is how the server reports a tag written before purpose types existed. */
  purposeType?: MonitoringPromptPurposeType | null;
  purposeKpis?: readonly MonitoringPromptKpi[] | null;
}

/** One rendered tab. */
export interface PurposeTab {
  key: string;
  name: string;
  /** The term's id — what the link that sends an operator to type the tag needs. */
  id?: string;
  presentation: TermStatusPresentation;
  /**
   * What the purpose is for, once the tag has been typed.
   *
   * Absent on a tag written before purpose types existed. The page blocks that tab (see
   * `isSelectedPurposeUntyped`) rather than showing every KPI: nothing says which of them measure
   * anything for those prompts.
   */
  purposeType?: MonitoringPromptPurposeType;
  /** The tag's own KPI list — only a `custom` type carries one. */
  purposeKpis?: readonly MonitoringPromptKpi[];
}

/**
 * Rank a priority for descending sort. Higher is more important.
 *
 * The enum's values carry a numeric prefix (`400-critical` … `100-low`) precisely so they order
 * themselves, so the prefix is read rather than a lookup table maintained beside it — a new
 * priority slots in without touching this.
 *
 * A term with NO priority ranks below every stated one. That is the common case rather than an edge
 * case: most purpose terms on a real project carry no priority at all, and they must not outrank a
 * purpose an operator deliberately marked critical.
 */
export function purposePriorityRank(priority: TaxonomyTermPriority | undefined): number {
  const prefix = Number.parseInt(String(priority ?? '').split('-')[0], 10);
  return Number.isFinite(prefix) ? prefix : -1;
}

/**
 * Build the tab list: most important first, then alphabetical.
 *
 * Archived purposes are never offered. The exception is one that is currently selected — a saved
 * view or deep link may name a purpose since archived, and dropping it would silently switch the
 * operator to a different purpose's data while the URL still said otherwise. It is kept as a tab so
 * the state stays honest, but never offered to anyone who has not already got it.
 */
export function buildPurposeTabs(terms: readonly PurposeTermLike[] | undefined, selectedKey?: string): PurposeTab[] {
  const selected = String(selectedKey ?? '').trim();
  const tabs: (PurposeTab & { rank: number })[] = [];

  for (const term of terms ?? []) {
    const key = String(term?.key ?? '').trim();
    const name = String(term?.name ?? '').trim();
    if (!key || !name) {
      continue;
    }

    const presentation = termStatusPresentation(term?.status, 'option');
    if (presentation.hidden && key !== selected) {
      continue;
    }

    const id = term?.id == null ? undefined : String(term.id);
    tabs.push({
      key,
      name,
      id,
      presentation,
      // The server's `null` for a legacy tag and a missing field mean the same thing here: untyped.
      purposeType: term?.purposeType ?? undefined,
      purposeKpis: term?.purposeKpis ?? undefined,
      rank: purposePriorityRank(term?.priority),
    });
  }

  return tabs.sort((left, right) => right.rank - left.rank || left.name.localeCompare(right.name)).map(({ rank: _rank, ...tab }) => tab);
}

/**
 * The purpose the page should show, given the tabs and whatever the URL asked for.
 *
 * With no parameter the page opens on the first tab — the highest-priority purpose — rather than
 * unfiltered, because an unfiltered view mixes purposes whose metrics are not comparable. A
 * parameter naming a purpose that is not a tab falls back the same way rather than showing nothing.
 */
export function resolveSelectedPurposeKey(tabs: readonly PurposeTab[], requestedKey?: string): string | undefined {
  const requested = String(requestedKey ?? '').trim();
  if (requested && tabs.some((tab) => tab.key === requested)) {
    return requested;
  }
  return tabs[0]?.key;
}

/** The tab the page is on, when the key names one. */
export function selectedPurposeTab(tabs: readonly PurposeTab[], selectedKey?: string): PurposeTab | undefined {
  const selected = String(selectedKey ?? '').trim();
  return selected ? tabs.find((tab) => tab.key === selected) : undefined;
}

/**
 * Whether the selected tab's tag still lacks a purpose type.
 *
 * True only for a tab that exists AND is untyped: the page blocks its KPI surfaces until an operator
 * types the tag. A key that names no tab (the tabs have not loaded, or the URL is stale) answers
 * `false`, so the page keeps resolving the selection exactly as it does today.
 */
export function isSelectedPurposeUntyped(tabs: readonly PurposeTab[], selectedKey?: string): boolean {
  const tab = selectedPurposeTab(tabs, selectedKey);
  return !!tab && !tab.purposeType;
}
